<template>
  <b-modal 
    id="modal-completion-detail-user" 
    centered 
    hide-footer 
    title="Completion Detail"
    size="xl"
    body-class="p-0"
    @hidden="resetModal">
    
    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-danger" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Memuat detail completion...</p>
    </div>

    <!-- Content -->
    <div v-else class="row g-0">
      <!-- Left Side - Certificate Preview -->
      <div class="col-md-7 p-4 border-end">
        <!-- Status Badge -->
        <div class="mb-3">
          <span :class="getStatusBadgeClass(completionData?.status)" style="padding: 0.35rem 0.75rem;">
            <i class="fe fe-info me-1"></i>
            {{ completionData?.status || 'Pending' }}
          </span>
        </div>

        <!-- Company Info -->
        <div class="mb-4 p-3 rounded" style="background-color: #fafafa;">
          <div class="d-flex align-items-center">
            <div class="company-icon me-3">
              <i class="fe fe-briefcase text-danger" style="font-size: 1.5rem;"></i>
            </div>
            <div>
              <h6 class="mb-1 font-weight-bold">{{ completionData?.company || '-' }}</h6>
              <small class="text-muted">
                <i class="fe fe-users me-1"></i>
                {{ completionData?.uploading_certificate_count || 0 }} sertifikat diupload
              </small>
            </div>
          </div>
        </div>

        <!-- Certificate Preview -->
        <div class="certificate-section">
          <h6 class="font-weight-bold mb-3">Preview Sertifikat</h6>
          
          <!-- No Certificate -->
          <div v-if="!completionData?.certificate_url" class="text-center py-5">
            <i class="fe fe-file-text text-muted" style="font-size: 4rem;"></i>
            <p class="text-muted mt-3 mb-0">Belum ada sertifikat yang diupload.</p>
          </div>

          <!-- PDF Certificate -->
          <div v-else-if="isPdfCertificate(completionData?.certificate_url)" 
               class="certificate-container rounded" 
               style="cursor: pointer;"
               @click="previewCertificate">
            <div class="pdf-overlay">
              <div class="pdf-icon-container-large">
                <i class="fe fe-file-text"></i>
              </div>
              <p class="pdf-label mt-3 mb-1">Sertifikat PDF</p>
              <small class="text-muted">Klik untuk melihat dokumen lengkap.</small>
            </div>
          </div>

          <!-- Image Certificate -->
          <div v-else class="certificate-container rounded" style="cursor: pointer;" @click="previewCertificate">
            <img 
              :src="`${imageBaseUrl}/attachments/${completionData?.certificate_url}`" 
              alt="Certificate"
              class="certificate-image"
              @error="handleCertImageError">
          </div>

          <!-- Notes Section -->
          <div v-if="completionData?.notes" class="mt-3 p-3 rounded" style="background-color: #fafafa;">
            <label class="text-muted small mb-1">Your Notes</label>
            <p class="mb-0">{{ completionData?.notes }}</p>
          </div>
        </div>
      </div>

      <!-- Right Side - Review Chat & Upload -->
      <div class="col-md-5 p-4" style="background-color: #fafafa;">
        <h6 class="font-weight-bold mb-3">
          <i class="fe fe-message-square me-2"></i>
          Admin Feedback & Reply
        </h6>

        <!-- Review Chat Box -->
        <div class="review-chat-container mb-3" ref="chatContainer">
          <!-- Empty State -->
          <div v-if="!completionData?.reviews || completionData?.reviews.length === 0" class="text-center py-5">
            <i class="fe fe-message-circle text-muted" style="font-size: 2.5rem;"></i>
            <p class="text-muted mt-2 mb-0 small">No feedback yet</p>
          </div>

          <!-- Review Messages -->
          <div v-else class="review-messages">
            <div 
              v-for="(review, index) in completionData?.reviews" 
              :key="index"
              class="review-item mb-3"
              :class="{
                'review-item-partner': review.role === 'adminmitra',
                'review-item-admin': review.role === 'hsetraining'
              }">
              <div class="review-header mb-1" :class="{
                'text-end': review.role === 'adminmitra'
              }">
                <strong class="text-dark">{{ review.created_by?.name || 'User' }}</strong>
                <small class="text-muted ms-2">{{ formatDate(review.created_at) }}</small>
              </div>
              <div class="review-body p-3 rounded" :class="{
                'review-body-partner': review.role === 'adminmitra',
                'review-body-admin': review.role === 'hsetraining'
              }">
                <p class="mb-0">{{ review.feedback || review.comment }}</p>
                <div v-if="review.is_uploading_new_certificate" class="mt-2 pt-2 border-top">
                  <small class="text-muted">
                    <i class="fe fe-paperclip me-1"></i>
                    New certificate uploaded
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Reply Form -->
        <div class="reply-form">
          <h6 class="font-weight-bold mb-3 small">Reply to Admin</h6>
          
          <!-- Certificate Upload -->
          <div class="mb-3">
            <label class="form-label small font-weight-bold">
              Upload New Certificate (Optional)
            </label>
            <div class="custom-file-upload">
              <input 
                type="file" 
                ref="certificateInput"
                @change="handleFileChange"
                accept="image/*,.pdf"
                class="d-none"
                id="certificate-upload-input">
              <label for="certificate-upload-input" class="file-upload-label">
                <i class="fe fe-upload me-2"></i>
                {{ selectedFileName || 'Choose certificate file...' }}
              </label>
              <small class="text-muted d-block mt-1">Supported: JPG, PNG, PDF (Max 5MB)</small>
            </div>
          </div>

          <!-- Feedback Text -->
          <div class="mb-3">
            <label class="form-label font-weight-bold small">
              Your Message
            </label>
            <textarea 
              v-model="feedback"
              class="form-control mb-2"
              rows="3"
              placeholder="Enter your response or explanation..."
              :disabled="isSubmitting"
              maxlength="500">
            </textarea>
            <div class="d-flex justify-content-between align-items-center">
              <small class="text-muted">{{ feedback.length }}/500 characters</small>
            </div>
          </div>

          <!-- Submit Button -->
          <button 
            class="btn btn-danger btn-block"
            @click="submitReply"
            :disabled="(!feedback.trim() && !selectedFile) || isSubmitting">
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
            {{ isSubmitting ? 'Mengirim...' : 'Submit Reply' }}
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { formatDate } from '@/utils/dateFormatter';
import { normalizeErrorMessage } from '@/utils/error-message';

export default {
  name: 'ModalCompletionDetailUser',
  props: {
    completionId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV,
      completionData: null,
      feedback: '',
      selectedFile: null,
      selectedFileName: '',
      isLoading: false,
      isSubmitting: false
    }
  },
  watch: {
    completionId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.loadCompletionDetail();
        }
      }
    }
  },
  methods: {
    formatDate,

    async loadCompletionDetail() {
      if (!this.completionId) return;

      this.isLoading = true;
      try {
        const courseCompletionMitraAPI = await import('@/api/course-completion-mitra/course-completion-mitra.js');
        const response = await courseCompletionMitraAPI.default.getCompletionDetail(this.completionId);
        this.completionData = response.data.data;
        
        // Scroll to bottom of chat after loading
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error('Error loading completion detail:', error);
        this.$swal({
          title: 'Error',
          text: 'Detail completion belum berhasil dimuat.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.isLoading = false;
      }
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.$swal({
            title: 'Error',
            text: 'Ukuran file maksimal 5MB.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
          this.$refs.certificateInput.value = '';
          return;
        }

        this.selectedFile = file;
        this.selectedFileName = file.name;
      }
    },

    async submitReply() {
      if (!this.feedback.trim() && !this.selectedFile) return;

      this.isSubmitting = true;
      try {
        const formData = new FormData();
        
        if (this.feedback.trim()) {
          formData.append('feedback', this.feedback.trim());
        }
        
        if (this.selectedFile) {
          formData.append('file', this.selectedFile);
        }

        const courseCompletionMitraAPI = await import('@/api/course-completion-mitra/course-completion-mitra.js');
        await courseCompletionMitraAPI.default.submitCompletionReview(this.completionId, formData);

        this.$swal({
          title: 'Success',
          text: 'Balasan berhasil dikirim.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        // Reload completion detail to get updated reviews
        await this.loadCompletionDetail();
        
        // Reset form
        this.feedback = '';
        this.selectedFile = null;
        this.selectedFileName = '';
        if (this.$refs.certificateInput) {
          this.$refs.certificateInput.value = '';
        }

        // Emit event to parent to refresh data
        this.$emit('reply-submitted');
      } catch (error) {
        console.error('Error submitting reply:', error);
        this.$swal({
          title: 'Error',
          text: normalizeErrorMessage(error.response?.data?.message || 'Balasan belum berhasil dikirim'),
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    isPdfCertificate(certificateUrl) {
      if (!certificateUrl) return false;
      const lowerUrl = certificateUrl.toLowerCase();
      return lowerUrl.endsWith('.pdf');
    },

    previewCertificate() {
      if (this.completionData?.certificate_url) {
        const certUrl = `${this.imageBaseUrl}/attachments/${this.completionData.certificate_url}`;
        window.open(certUrl, '_blank');
      }
    },

    handleCertImageError(event) {
      event.target.src = 'https://dummyimage.com/600x400/FAFAFA/000000&text=Certificate';
    },

    getStatusBadgeClass(status) {
      const statusLower = status?.toLowerCase() || '';
      
      if (statusLower.includes('approved') || statusLower.includes('completed')) {
        return 'badge bg-success';
      } else if (statusLower.includes('rejected') || statusLower.includes('failed')) {
        return 'badge bg-danger';
      } else if (statusLower.includes('waiting') || statusLower.includes('pending')) {
        return 'badge bg-warning';
      } else if (statusLower.includes('review')) {
        return 'badge bg-info';
      }
      return 'badge bg-secondary';
    },

    scrollToBottom() {
      if (this.$refs.chatContainer) {
        const container = this.$refs.chatContainer;
        container.scrollTop = container.scrollHeight;
      }
    },

    resetModal() {
      this.completionData = null;
      this.feedback = '';
      this.selectedFile = null;
      this.selectedFileName = '';
      this.isLoading = false;
      this.isSubmitting = false;
      if (this.$refs.certificateInput) {
        this.$refs.certificateInput.value = '';
      }
    }
  }
}
</script>

<style scoped>
/* Certificate Container */
.certificate-container {
  position: relative;
  width: 100%;
  padding-top: 66.67%; /* 3:2 aspect ratio */
  overflow: hidden;
  background-color: #fff;
  border: 1px solid #dee2e6;
}

.certificate-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.2s ease;
}

.certificate-container:hover .certificate-image {
  transform: scale(1.02);
}

/* PDF Overlay */
.pdf-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
  transition: background-color 0.2s ease;
}

.certificate-container:hover .pdf-overlay {
  background-color: #f0f0f0;
}

.pdf-icon-container-large {
  width: 100px;
  height: 130px;
  margin: 0 auto;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-icon-container-large i {
  font-size: 3.5rem;
  color: #fff;
}

.pdf-label {
  font-weight: 600;
  color: #1a202c;
  font-size: 1rem;
}

/* Review Chat */
.review-chat-container {
  height: 250px;
  overflow-y: auto;
  padding: 1rem;
  background-color: white;
  border-radius: 0.5rem;
  border: 1px solid #dee2e6;
}

.review-messages {
  display: flex;
  flex-direction: column;
}

.review-item {
  animation: fadeIn 0.3s ease;
  display: flex;
  flex-direction: column;
}

.review-item-partner {
  align-items: flex-end;
}

.review-item-admin {
  align-items: flex-start;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.review-header {
  display: flex;
  align-items: center;
  padding: 0 0.25rem;
}

.review-body {
  word-wrap: break-word;
  max-width: 80%;
  border: 1px solid #e9ecef;
}

.review-body-partner {
  background-color: #e3f2fd !important;
  border-color: #90caf9 !important;
  color: #1565c0;
}

.review-body-admin {
  background-color: #fce4e4 !important;
  border-color: #f5c6cb !important;
  color: #721c24;
}

/* Company Icon */
.company-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #fce4e4;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* File Upload */
.custom-file-upload {
  position: relative;
}

.file-upload-label {
  display: block;
  padding: 0.75rem 1rem;
  background-color: white;
  border: 2px dashed #dee2e6;
  border-radius: 0.375rem;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 0;
}

.file-upload-label:hover {
  border-color: #e63757;
  color: #e63757;
  background-color: #fafafa;
}

/* Badges */
.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.35rem 0.75rem;
  border-radius: 0.35rem;
}

.bg-success {
  background-color: #00d97e !important;
}

.bg-danger {
  background-color: #e63757 !important;
}

.bg-warning {
  background-color: #f6c343 !important;
}

.bg-info {
  background-color: #39afd1 !important;
}

.bg-secondary {
  background-color: #6c757d !important;
}

/* Utilities */
.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.me-3 {
  margin-right: 1rem;
}

.ms-2 {
  margin-left: 0.5rem;
}

/* Scrollbar */
.review-chat-container::-webkit-scrollbar {
  width: 6px;
}

.review-chat-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.review-chat-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.review-chat-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
